import { Platform } from 'react-native'
import { router } from 'expo-router'

/**
 * The route the visitor picked on the chat's out-of-money card. `target` is the
 * agent's own slug, `<family>.<item>`: the family is one cashier this shell
 * already has, the item is what to open it on. Consumed as-is — the chat has
 * already decided the route.
 */
export type ChatPurchase = { target?: string }

/**
 * The packs this shell can take money for.
 *
 * `sub_account` is missing on purpose: its price depends on the plan term left,
 * which the desktop's own modal works out and the mobile cashier does not — it
 * bills the published packages flat. Selling it here would overcharge, so the
 * chat is told this shell does not sell it and the button stays undrawn.
 */
const SELLABLE_PACKS = ['ai_points', 'sms', 'active_mail', 'storage_quota', 'audio_quota']

/**
 * What this shell handles, declared to the chat as `?caps=` on its URL. The chat
 * draws a button only for what is listed, so a token joins this list in the
 * release that handles it — never earlier.
 *
 * Declared per target rather than per family, because this shell is short of
 * parts of one: the packs above sell here and `sub_account` does not.
 *
 * iOS declares the plan family alone. Apple's rules keep consumables off a
 * webview cashier, and the balance entry does not exist there at all — but plans
 * are sold in-app through the App Store, so an upgrade is honoured, which the
 * whole-capability token this replaces could not have said.
 *
 * Nothing here names the pre-rename token: a store release is the last of this
 * feature's to ship, so the chat in front of it is the renamed one by the time
 * this reaches anybody. Declaring it would only promise iOS's packs to a chat
 * that no longer asks.
 *
 * Mobile web is not the App and is not affected — `Platform.OS` is `web` there.
 */
export const CHAT_CAPS =
  Platform.OS === 'ios'
    ? 'purchase.plan'
    : [...SELLABLE_PACKS.map(pack => `purchase.vas.${pack}`), 'purchase.balance', 'purchase.plan'].join(',')

function openCashier(cashierParams: Record<string, string>) {
  router.push({
    pathname: '/profile/cashier',
    params: { cashierParams: btoa(encodeURIComponent(JSON.stringify(cashierParams))) },
  })
}

/**
 * Open this shell's own cashier for the route the visitor picked in the chat.
 *
 * Routing is on the target's family — one family, one of our surfaces — and the
 * suffix picks the item within it. `plan` and `balance` have a single surface
 * each, so any suffix under them lands there; that is what lets the agent name a
 * new one without this shell shipping first.
 *
 * The cashiers title themselves from their own published packages, so nothing
 * here restates a name, a quantity or a price.
 */
export function handleChatPurchase(purchase: ChatPurchase) {
  const target = purchase.target
  if (!target) return

  const separator = target.indexOf('.')
  if (separator < 0) return
  const family = target.slice(0, separator)
  const item = target.slice(separator + 1)

  if (family === 'plan') {
    // Plans are the one thing iOS sells in-app, and it sells them through the
    // App Store rather than the webview cashier every other platform opens.
    router.push(Platform.OS === 'ios' ? '/profile/iosPaywall' : '/profile/plans')
    return
  }

  if (family === 'balance') {
    openCashier({ cashierType: 'recharge' })
    return
  }

  if (family !== 'vas') return
  if (!SELLABLE_PACKS.includes(item)) return

  openCashier({ cashierType: 'vas', vasType: item })
}
